import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import type { NormalizedDateFilter } from '@/components/DatePicker/RichDatePicker';
import { PaymentFilterBar } from '@/features/contract-and-cost/deal';
import { useDateRangeFilter } from '@/hooks/useDateRangeFilter';
import { usePaginationSearch } from '@/lib/listPage';
import { PaymentDealTable } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/payment/-components/PaymentDealTable';
import { usePaymentDealList } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/payment/-query';
const ALL_STATUSES = ['Scheduled', 'Completed'] as const;
const WITHOUT_COMPLETED = ['Scheduled'] as const;
const SearchSchema = z.object({
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  freeText: z.string().optional(),
  hidePaid: z.boolean().default(false),
  paymentDateFrom: z.string().optional(),
  paymentDateTo: z.string().optional(),
  paymentDeadlineFrom: z.string().optional(),
  paymentDeadlineTo: z.string().optional()
});
export const Route = createFileRoute('/_staff/software/$id/_$id/contract-and-cost/_contract-and-cost/deal/_deal/payment/')({
  component: PaymentTab,
  validateSearch: zodValidator(SearchSchema)
});
function PaymentTab() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: s => navigate({
      search: s
    })
  });
  const statuses = useMemo(() => search.hidePaid ? [...WITHOUT_COMPLETED] : [...ALL_STATUSES], [search.hidePaid]);
  const {
    data,
    isPending
  } = usePaymentDealList({
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    freeText: search.freeText,
    statuses
  });
  const {
    currentFilter: paymentDateFilter,
    handleFilterChange: handlePaymentDateQueryChange
  } = useDateRangeFilter(search.paymentDateFrom, search.paymentDateTo);
  const handlePaymentDateFilterChange = useCallback(({
    normalized
  }: {
    normalized?: NormalizedDateFilter;
  }) => {
    const {
      fromDate,
      toDate
    } = handlePaymentDateQueryChange(normalized);
    navigate({
      search: prev => ({
        ...prev,
        paymentDateFrom: fromDate,
        paymentDateTo: toDate,
        pageIndex: 0
      })
    });
  }, [navigate, handlePaymentDateQueryChange]);
  const {
    currentFilter: paymentDeadlineFilter,
    handleFilterChange: handlePaymentDeadlineQueryChange
  } = useDateRangeFilter(search.paymentDeadlineFrom, search.paymentDeadlineTo);
  const handlePaymentDeadlineFilterChange = useCallback(({
    normalized
  }: {
    normalized?: NormalizedDateFilter;
  }) => {
    const {
      fromDate,
      toDate
    } = handlePaymentDeadlineQueryChange(normalized);
    navigate({
      search: prev => ({
        ...prev,
        paymentDeadlineFrom: fromDate,
        paymentDeadlineTo: toDate,
        pageIndex: 0
      })
    });
  }, [navigate, handlePaymentDeadlineQueryChange]);
  const handleHidePaidChange = useCallback((checked: boolean) => {
    navigate({
      search: prev => ({
        ...prev,
        hidePaid: checked,
        pageIndex: 0
      })
    });
  }, [navigate]);
  return <>
      <PaymentFilterBar searchValue={search.freeText ?? ''} onSearchChange={e => navigate({
      search: prev => ({
        ...prev,
        freeText: e.target.value || undefined,
        pageIndex: 0
      })
    })} paymentDateFilter={paymentDateFilter} onPaymentDateFilterChange={handlePaymentDateFilterChange} paymentDeadlineFilter={paymentDeadlineFilter} onPaymentDeadlineFilterChange={handlePaymentDeadlineFilterChange} hidePaid={search.hidePaid} onHidePaidChange={handleHidePaidChange} onExport={() => {}} />
      <PaymentDealTable data={data?.items ?? []} isPending={isPending} totalCount={data?.count} pageCount={data?.maxPage} pagination={pagination} onPaginationChange={setPagination} hideSoftwareColumn className="flex-1 min-h-0" />
    </>;
}